import type { AgendaHandle, NavigationKernel } from '@/shared/navigation/core';
import { useWindowManagerStore } from '@/shared/windowing';
import { jobDetailAgenda } from '../detail/jobDetailAgenda';
import { jobsOverviewAgenda } from '../overview/jobsOverviewAgenda';

/**
 * Otevírání jobs oken standardním vzorem osamostatněného okna (openModal + fresh stackId,
 * viz `openNativeDetached` v shellu) + výchozí geometrie dle designu. Geometrie se nastavuje
 * synchronně hned po přiřazení stackId — předběhne tak seed efekt PhoenixDesktopu (ten okna
 * s existujícím rectem přeskakuje).
 */

function openDetachedWithRect(
  kernel: NavigationKernel,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generická agenda handle, stejně jako v AgendaRegistration
  agenda: AgendaHandle<any, any>,
  params: Record<string, unknown>,
  rectFor: (desktop: { w: number; h: number }) => { x: number; y: number; w: number; h: number }
): void {
  void kernel.openModal(agenda, params).then(() => {
    const layerId = kernel.getState().activeTopLayerId;
    if (!layerId) return;
    kernel.setLayerStackId(layerId, `s_${crypto.randomUUID()}`);
    const wm = useWindowManagerStore.getState();
    wm.setMode(layerId, 'floating');
    wm.setRect(layerId, rectFor(wm.desktopSize));
    wm.setActive(layerId);
  });
}

/** Vrstva centrálního přehledu, pokud je otevřená. */
export function findOverviewLayer(kernel: NavigationKernel) {
  return kernel.getState().layers.find((l) => l.agendaId === jobsOverviewAgenda.id);
}

/** Vrstva detailu daného runu, pokud je otevřená. */
export function findDetailLayer(kernel: NavigationKernel, runId: string) {
  return kernel
    .getState()
    .layers.find((l) => l.agendaId === jobDetailAgenda.id && l.openParams['runId'] === runId);
}

/** RunId všech otevřených detailů jobů. */
export function openDetailRunIds(kernel: NavigationKernel): string[] {
  return kernel
    .getState()
    .layers.filter((l) => l.agendaId === jobDetailAgenda.id)
    .map((l) => l.openParams['runId'])
    .filter((id): id is string => typeof id === 'string');
}

/** Otevře centrální přehled „Operace na pozadí" (floating vpravo, ~80 % výšky, dle designu). */
export function openJobsOverviewWindow(kernel: NavigationKernel): void {
  openDetachedWithRect(kernel, jobsOverviewAgenda, {}, ({ w, h }) => {
    const width = Math.min(520, w - 32);
    return { x: w - width - 16, y: Math.round(h * 0.1), w: width, h: Math.round(h * 0.8) };
  });
}

/**
 * Toggle centrálního přehledu z OpsIndicatoru: zavřené → otevřít; minimalizované → obnovit
 * a fokusovat; jinak zavřít (chování dle designu).
 */
export function toggleJobsOverviewWindow(kernel: NavigationKernel): void {
  const layer = findOverviewLayer(kernel);
  if (!layer) {
    openJobsOverviewWindow(kernel);
    return;
  }
  const wm = useWindowManagerStore.getState();
  if (wm.windows[layer.layerId]?.mode === 'minimized') {
    wm.restore(layer.layerId);
    if (layer.stackId) wm.raiseStack(layer.stackId);
    wm.setActive(layer.layerId);
    return;
  }
  void kernel.closeLayer(layer.layerId);
}

/** Otevře (nebo fokusne existující) detail jobu — okno ~790px, vycentrované mírně vlevo. */
export function openJobDetailWindow(kernel: NavigationKernel, runId: string): void {
  const existing = findDetailLayer(kernel, runId);
  if (existing) {
    const wm = useWindowManagerStore.getState();
    if (wm.windows[existing.layerId]?.mode === 'minimized') wm.restore(existing.layerId);
    if (existing.stackId) wm.raiseStack(existing.stackId);
    wm.setActive(existing.layerId);
    return;
  }
  openDetachedWithRect(kernel, jobDetailAgenda, { runId }, ({ w, h }) => {
    const width = Math.min(790, w - 24);
    const height = Math.round(h * 0.84);
    return {
      x: Math.max(12, Math.round(w / 2 - 515)),
      y: Math.round((h - height) / 2),
      w: width,
      h: height,
    };
  });
}
